import { faker } from '@faker-js/faker';

// Define marketing performance interface
export interface MarketingPerformance {
  leads: number;
  qualified: number;
  warm: number;
  cold: number;
  costPerLead: number;
  conversionRate: number;
  returnOnMarketingSpend: number;
  renewals: number;
  monthlyTrend: {
    month: string;
    leads: number;
    qualified: number;
    costPerLead: number;
    conversionRate: number;
    roms: number;
  }[];
  channelPerformance: {
    channel: string;
    leads: number;
    qualified: number;
    conversion: number;
    cost: number;
    color: string;
  }[];
}

// Define interface for business key metrics
export interface BusinessKeyMetrics {
  northStarMetric: {
    name: string;
    value: number;
    change: number;
    goal: number;
    unit: string;
  };
  averageRevenuePerCustomer: number;
  customerAcquisitionCost: number;
  lifetimeValue: number;
  grossMargin: number;
  revenueGrowthRate: number;
  grossMerchandiseValue: number;
  churnRate: number;
  totalRevenue: number;
  annualRecurringRevenue: number;
  monthlyRecurringRevenue: number;
  historicalData: {
    month: string;
    arpu: number;
    cac: number;
    ltv: number;
    churn: number;
    margin: number;
  }[];
}

// Define interfaces for various superadmin data types
export interface SystemMetric {
  id: string;
  name: string;
  value: number;
  change: number;
  isPositive: boolean;
  icon?: string;
  trend?: number[];
}

export interface UserMetrics {
  totalUsers: number;
  activeUsers: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  roleDistribution: {
    label: string;
    value: number;
    color: string;
  }[];
  activityByHour: number[];
}

export interface SystemHealth {
  status: 'healthy' | 'warning' | 'critical';
  uptime: number; // in hours
  responseTime: number; // in ms
  errors24h: number;
  cpuUsage: number; // percentage
  memoryUsage: number; // percentage
  diskUsage: number; // percentage
  alertsCount: number;
  recentIncidents: SystemIncident[];
}

export interface SystemIncident {
  id: string;
  timestamp: string;
  type: 'error' | 'warning' | 'info';
  message: string;
  service: string;
  resolved: boolean;
}

export interface PlatformStats {
  totalPolicies: number;
  activePolicies: number;
  totalLoans: number;
  activeLoans: number;
  totalTransactions: number;
  transactionVolume: number; // in USD
  newPartnersThisMonth: number;
  conversionRate: number; // percentage
}

// Generate mock user metrics
export const generateUserMetrics = (): UserMetrics => {
  const totalUsers = faker.number.int({ min: 5000, max: 10000 });
  const activeUsers = faker.number.int({ min: 2000, max: totalUsers - 1000 });
  const newUsersToday = faker.number.int({ min: 10, max: 100 });
  const newUsersThisWeek = newUsersToday + faker.number.int({ min: 50, max: 300 });
  
  const activityByHour = Array.from({ length: 24 }, () => 
    faker.number.int({ min: 10, max: 500 })
  );
  
  return {
    totalUsers,
    activeUsers,
    newUsersToday,
    newUsersThisWeek,
    roleDistribution: [
      { label: 'Customers', value: faker.number.int({ min: 3000, max: 5000 }), color: '#4f46e5' },
      { label: 'Brokers', value: faker.number.int({ min: 500, max: 1000 }), color: '#10b981' },
      { label: 'Underwriters', value: faker.number.int({ min: 100, max: 200 }), color: '#f59e0b' },
      { label: 'Finance Partners', value: faker.number.int({ min: 50, max: 100 }), color: '#ef4444' },
      { label: 'Admins', value: faker.number.int({ min: 10, max: 30 }), color: '#8b5cf6' }
    ],
    activityByHour
  };
};

// Generate mock system health data
export const generateSystemHealth = (): SystemHealth => {
  const cpuUsage = faker.number.float({ min: 10, max: 95, fractionDigits: 2 });
  const memoryUsage = faker.number.float({ min: 20, max: 90, fractionDigits: 2 });
  const diskUsage = faker.number.float({ min: 30, max: 85, fractionDigits: 2 });
  const errors24h = faker.number.int({ min: 0, max: 100 });
  
  let status: 'healthy' | 'warning' | 'critical' = 'healthy';
  if (cpuUsage > 80 || memoryUsage > 80 || diskUsage > 85 || errors24h > 50) {
    status = 'critical';
  } else if (cpuUsage > 60 || memoryUsage > 60 || diskUsage > 70 || errors24h > 20) {
    status = 'warning';
  }
  
  return {
    status,
    uptime: faker.number.float({ min: 100, max: 10000, fractionDigits: 2 }),
    responseTime: faker.number.float({ min: 50, max: 500, fractionDigits: 2 }),
    errors24h,
    cpuUsage,
    memoryUsage,
    diskUsage,
    alertsCount: faker.number.int({ min: 0, max: 10 }),
    recentIncidents: Array.from({ length: faker.number.int({ min: 0, max: 5 }) }, () => generateIncident())
  };
};

// Generate a single incident
const generateIncident = (): SystemIncident => {
  const types = ['error', 'warning', 'info'] as const;
  const services = ['API Server', 'Database', 'Authentication', 'Payment Gateway', 'Notification Service', 'File Storage'];
  
  return {
    id: faker.string.uuid(),
    timestamp: faker.date.recent({ days: 7 }).toISOString(),
    type: types[faker.number.int({ min: 0, max: 2 })],
    message: faker.lorem.sentence(),
    service: services[faker.number.int({ min: 0, max: services.length - 1 })],
    resolved: faker.datatype.boolean()
  };
};

// Generate platform statistics
export const generatePlatformStats = (): PlatformStats => {
  const totalPolicies = faker.number.int({ min: 10000, max: 50000 });
  const activePolicies = faker.number.int({ min: 8000, max: totalPolicies - 1000 });
  const totalLoans = faker.number.int({ min: 5000, max: 20000 });
  const activeLoans = faker.number.int({ min: 4000, max: totalLoans - 500 });
  const totalTransactions = faker.number.int({ min: 50000, max: 200000 });
  const transactionVolume = faker.number.int({ min: 5000000, max: 20000000 });
  
  return {
    totalPolicies,
    activePolicies,
    totalLoans,
    activeLoans,
    totalTransactions,
    transactionVolume,
    newPartnersThisMonth: faker.number.int({ min: 5, max: 30 }),
    conversionRate: faker.number.float({ min: 5, max: 25, fractionDigits: 1 })
  };
};

// Generate key system metrics
export const generateSystemMetrics = (): SystemMetric[] => {
  return [
    {
      id: 'users',
      name: 'Total Users',
      value: faker.number.int({ min: 5000, max: 10000 }),
      change: faker.number.float({ min: -5, max: 25, fractionDigits: 1 }),
      isPositive: true,
      icon: 'users',
      trend: Array.from({ length: 7 }, () => faker.number.int({ min: 4500, max: 10000 }))
    },
    {
      id: 'policies',
      name: 'Active Policies',
      value: faker.number.int({ min: 8000, max: 50000 }),
      change: faker.number.float({ min: -3, max: 15, fractionDigits: 1 }),
      isPositive: true,
      icon: 'file-text',
      trend: Array.from({ length: 7 }, () => faker.number.int({ min: 7000, max: 50000 }))
    },
    {
      id: 'revenue',
      name: 'Monthly Revenue',
      value: faker.number.int({ min: 500000, max: 2000000 }),
      change: faker.number.float({ min: -5, max: 20, fractionDigits: 1 }),
      isPositive: true,
      icon: 'dollar-sign',
      trend: Array.from({ length: 7 }, () => faker.number.int({ min: 450000, max: 2000000 }))
    },
    {
      id: 'conversion',
      name: 'Conversion Rate',
      value: faker.number.float({ min: 5, max: 25, fractionDigits: 1 }),
      change: faker.number.float({ min: -2, max: 5, fractionDigits: 1 }),
      isPositive: true,
      icon: 'trending-up',
      trend: Array.from({ length: 7 }, () => faker.number.float({ min: 4, max: 26, fractionDigits: 1 }))
    },
    {
      id: 'tickets',
      name: 'Open Tickets',
      value: faker.number.int({ min: 20, max: 200 }),
      change: faker.number.float({ min: -30, max: 30, fractionDigits: 1 }),
      isPositive: false,
      icon: 'alert-circle',
      trend: Array.from({ length: 7 }, () => faker.number.int({ min: 15, max: 250 }))
    },
    {
      id: 'response',
      name: 'Avg Response Time',
      value: faker.number.float({ min: 50, max: 500, fractionDigits: 1 }),
      change: faker.number.float({ min: -30, max: 10, fractionDigits: 1 }),
      isPositive: true,
      icon: 'clock',
      trend: Array.from({ length: 7 }, () => faker.number.float({ min: 45, max: 550, fractionDigits: 1 }))
    }
  ];
};

// Generate revenue data by date for timeline chart
export const generateRevenueData = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentMonth = new Date().getMonth();
  
  return months.map((month, index) => {
    const isCurrentMonth = index === currentMonth;
    const isPastMonth = index < currentMonth;
    
    return {
      name: month,
      revenue: isPastMonth || isCurrentMonth ? 
        faker.number.int({ min: 300000, max: 2000000 }) : 
        null,
      expenses: isPastMonth || isCurrentMonth ? 
        faker.number.int({ min: 100000, max: 1200000 }) : 
        null,
      profit: isPastMonth || isCurrentMonth ? 
        faker.number.int({ min: 100000, max: 800000 }) : 
        null
    };
  });
};

// Generate active users data by time
export const generateActiveUsersData = () => {
  const hours = Array.from({ length: 24 }, (_, i) => `${i}:00`);
  
  return hours.map(hour => ({
    time: hour,
    users: faker.number.int({ min: 100, max: 1500 })
  }));
};

// Generate regional distribution data
export const generateRegionalData = () => {
  const regions = [
    { name: 'North America', color: '#4f46e5' },
    { name: 'Europe', color: '#10b981' },
    { name: 'Asia', color: '#f59e0b' },
    { name: 'South America', color: '#ef4444' },
    { name: 'Africa', color: '#8b5cf6' },
    { name: 'Oceania', color: '#06b6d4' }
  ];
  
  return regions.map(region => ({
    name: region.name,
    users: faker.number.int({ min: 100, max: 5000 }),
    policies: faker.number.int({ min: 50, max: 10000 }),
    revenue: faker.number.int({ min: 10000, max: 1000000 }),
    color: region.color
  }));
};

// Generate marketing performance data
export const generateMarketingPerformance = (): MarketingPerformance => {
  // Generate monthly trend data for the past 6 months
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const monthlyTrend = months.map(month => {
    const leads = faker.number.int({ min: 800, max: 1500 });
    const qualified = faker.number.int({ min: Math.floor(leads * 0.2), max: Math.floor(leads * 0.4) });
    const conversionRate = faker.number.float({ min: 8, max: 15, fractionDigits: 1 });
    const costPerLead = faker.number.float({ min: 20, max: 40, fractionDigits: 2 });
    const marketingSpend = leads * costPerLead;
    const revenue = qualified * (conversionRate / 100) * 200; // assuming $200 average sale
    const roms = (revenue / marketingSpend) * 100;

    return {
      month,
      leads,
      qualified,
      costPerLead,
      conversionRate,
      roms
    };
  });

  // Marketing channels
  const channels = [
    { channel: 'Social Media', color: '#4f46e5' },
    { channel: 'Search Ads', color: '#10b981' },
    { channel: 'Email', color: '#f59e0b' },
    { channel: 'Content', color: '#8b5cf6' },
    { channel: 'Referral', color: '#06b6d4' }
  ];
  
  const channelPerformance = channels.map(ch => ({
    channel: ch.channel,
    leads: faker.number.int({ min: 100, max: 500 }),
    qualified: faker.number.int({ min: 20, max: 200 }),
    conversion: faker.number.float({ min: 5, max: 20, fractionDigits: 1 }),
    cost: faker.number.float({ min: 15, max: 50, fractionDigits: 2 }),
    color: ch.color
  }));

  // Current month values (latest month)
  const currentMonth = monthlyTrend[monthlyTrend.length - 1];

  // Calculate totals
  const totalLeads = currentMonth.leads;
  const qualified = Math.floor(totalLeads * faker.number.float({ min: 0.25, max: 0.35 }));
  const warm = Math.floor(totalLeads * faker.number.float({ min: 0.15, max: 0.25 }));
  const cold = totalLeads - qualified - warm;
  
  return {
    leads: totalLeads,
    qualified,
    warm,
    cold,
    costPerLead: currentMonth.costPerLead,
    conversionRate: currentMonth.conversionRate,
    returnOnMarketingSpend: currentMonth.roms,
    renewals: faker.number.int({ min: 400, max: 800 }),
    monthlyTrend,
    channelPerformance
  };
};

// Generate key business metrics
// Define interface for finance and payments data
export interface FinancePaymentsData {
  // Billing success rates
  yearlyBillingSuccess: number;
  monthlyBillingSuccess: number;
  weeklyBillingSuccess: number;
  
  // Key financial metrics
  financeYield: number;
  financeYieldChange: number;
  financedAmount: number;
  financedAmountChange: number;
  outstanding: number;
  outstandingChange: number;
  periodExpenses: number;
  periodExpensesChange: number;
  nominalPL: number;
  nominalPLChange: number;
  overdue: number;
  overdueChange: number;
  badDebtProvisions: number;
  badDebtProvisionsChange: number;

  // Time-series data for charts
  yearlyData: {
    period: string;
    successRate: number;
    revenue: number;
    expenses: number;
    profit: number;
  }[];
  monthlyData: {
    period: string;
    successRate: number;
    revenue: number;
    expenses: number;
    profit: number;
  }[];
  weeklyData: {
    period: string;
    successRate: number;
    revenue: number;
    expenses: number;
    profit: number;
  }[];

  // Outstanding breakdown
  outstandingBreakdown: {
    name: string;
    value: number;
    color: string;
  }[];
}

// Generate finance and payments data
// Define interface for policy analytics data
export interface PolicyAnalyticsData {
  // Statistics
  totalPoliciesCount: number;
  totalPoliciesAmount: number;
  growthRateCount: number;
  growthRateAmount: number;
  
  // Time series data
  quarterlyData: {
    period: string;
    policyCount: number;
    policyAmount: number;
  }[];
  
  monthlyData: {
    period: string;
    policyCount: number;
    policyAmount: number;
  }[];
  
  weeklyData: {
    period: string;
    policyCount: number;
    policyAmount: number;
  }[];
  
  // Policy type breakdown
  policyTypeBreakdown: {
    type: string;
    count: number;
    amount: number;
    color: string;
  }[];
}

// Generate policy analytics data
export const generatePolicyAnalyticsData = (): PolicyAnalyticsData => {
  // Generate quarterly data (last 8 quarters)
  const currentYear = new Date().getFullYear();
  const quarters = [];
  for (let i = 0; i < 2; i++) { // 2 years (8 quarters)
    for (let q = 1; q <= 4; q++) {
      quarters.push(`Q${q} ${currentYear - i}`);
    }
  }
  quarters.reverse();
  
  const baseQuarterlyCount = faker.number.int({ min: 2000, max: 3000 });
  const quarterlyData = quarters.map((quarter, index) => {
    // Generate increasing trend
    const factor = 1 + (index * 0.08);
    const policyCount = Math.round(baseQuarterlyCount * factor);
    const policyAmount = policyCount * faker.number.float({ min: 300, max: 500, fractionDigits: 2 });
    
    return {
      period: quarter,
      policyCount,
      policyAmount
    };
  });
  
  // Generate monthly data (last 12 months)
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthlyData = [];
  
  for (let i = 11; i >= 0; i--) {
    const today = new Date();
    today.setMonth(today.getMonth() - i);
    const month = months[today.getMonth()];
    const year = today.getFullYear();
    
    const baseMonthlyCount = faker.number.int({ min: 500, max: 800 });
    const factor = 1 + (i * 0.04); // Growth factor
    const policyCount = Math.round(baseMonthlyCount * factor);
    const policyAmount = policyCount * faker.number.float({ min: 280, max: 520, fractionDigits: 2 });
    
    monthlyData.push({
      period: `${month} ${year}`,
      policyCount,
      policyAmount
    });
  }
  
  // Generate weekly data (last 8 weeks)
  const weeklyData = [];
  for (let i = 7; i >= 0; i--) {
    const baseWeeklyCount = faker.number.int({ min: 100, max: 200 });
    const factor = 1 + (i * 0.03); // Growth factor
    const policyCount = Math.round(baseWeeklyCount * factor);
    const policyAmount = policyCount * faker.number.float({ min: 250, max: 550, fractionDigits: 2 });
    
    weeklyData.push({
      period: `Week ${i + 1}`,
      policyCount,
      policyAmount
    });
  }
  weeklyData.reverse();
  
  // Policy type breakdown
  const policyTypes = [
    { type: 'Auto', color: '#4f46e5' },
    { type: 'Home', color: '#10b981' },
    { type: 'Life', color: '#f59e0b' },
    { type: 'Health', color: '#ef4444' },
    { type: 'Business', color: '#8b5cf6' },
    { type: 'Travel', color: '#06b6d4' }
  ];
  
  const policyTypeBreakdown = policyTypes.map(type => ({
    type: type.type,
    count: faker.number.int({ min: 500, max: 5000 }),
    amount: faker.number.int({ min: 100000, max: 1500000 }),
    color: type.color
  }));
  
  // Calculate growth rates based on the most recent periods compared to previous ones
  const latestQuarter = quarterlyData[quarterlyData.length - 1];
  const previousQuarter = quarterlyData[quarterlyData.length - 2];
  const growthRateCount = ((latestQuarter.policyCount - previousQuarter.policyCount) / previousQuarter.policyCount) * 100;
  const growthRateAmount = ((latestQuarter.policyAmount - previousQuarter.policyAmount) / previousQuarter.policyAmount) * 100;
  
  return {
    totalPoliciesCount: quarterlyData.slice(-4).reduce((sum, q) => sum + q.policyCount, 0),
    totalPoliciesAmount: quarterlyData.slice(-4).reduce((sum, q) => sum + q.policyAmount, 0),
    growthRateCount,
    growthRateAmount,
    quarterlyData,
    monthlyData,
    weeklyData,
    policyTypeBreakdown
  };
};

export const generateFinancePaymentsData = (): FinancePaymentsData => {
  // Generate yearly data
  const years = ['2021', '2022', '2023', '2024', '2025'];
  const yearlyData = years.map(year => {
    const revenue = faker.number.int({ min: 800000, max: 1200000 });
    const expenses = faker.number.int({ min: 600000, max: 900000 });
    const profit = revenue - expenses;
    
    return {
      period: year,
      successRate: faker.number.float({ min: 82, max: 98, fractionDigits: 1 }),
      revenue,
      expenses,
      profit
    };
  });

  // Generate monthly data
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthlyData = months.map(month => {
    const revenue = faker.number.int({ min: 60000, max: 100000 });
    const expenses = faker.number.int({ min: 40000, max: 80000 });
    const profit = revenue - expenses;
    
    return {
      period: month,
      successRate: faker.number.float({ min: 80, max: 98, fractionDigits: 1 }),
      revenue,
      expenses,
      profit
    };
  });

  // Generate weekly data
  const weeks = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
  const weeklyData = weeks.map(week => {
    const revenue = faker.number.int({ min: 15000, max: 25000 });
    const expenses = faker.number.int({ min: 10000, max: 20000 });
    const profit = revenue - expenses;
    
    return {
      period: week,
      successRate: faker.number.float({ min: 78, max: 99, fractionDigits: 1 }),
      revenue,
      expenses,
      profit
    };
  });

  // Generate outstanding breakdown
  const outstandingBreakdown = [
    { name: 'Current', value: faker.number.int({ min: 300000, max: 500000 }), color: '#10b981' },
    { name: '1-30 Days', value: faker.number.int({ min: 80000, max: 150000 }), color: '#f59e0b' },
    { name: '31-60 Days', value: faker.number.int({ min: 40000, max: 80000 }), color: '#ef4444' },
    { name: '61-90 Days', value: faker.number.int({ min: 20000, max: 40000 }), color: '#8b5cf6' },
    { name: '90+ Days', value: faker.number.int({ min: 10000, max: 30000 }), color: '#6b7280' }
  ];

  return {
    yearlyBillingSuccess: faker.number.float({ min: 90, max: 98, fractionDigits: 1 }),
    monthlyBillingSuccess: faker.number.float({ min: 88, max: 96, fractionDigits: 1 }),
    weeklyBillingSuccess: faker.number.float({ min: 85, max: 95, fractionDigits: 1 }),
    
    financeYield: faker.number.float({ min: 6, max: 12, fractionDigits: 1 }),
    financeYieldChange: faker.number.float({ min: -1, max: 2, fractionDigits: 1 }),
    
    financedAmount: faker.number.int({ min: 2000000, max: 3000000 }),
    financedAmountChange: faker.number.float({ min: 3, max: 15, fractionDigits: 1 }),
    
    outstanding: faker.number.int({ min: 500000, max: 800000 }),
    outstandingChange: faker.number.float({ min: -5, max: 8, fractionDigits: 1 }),
    
    periodExpenses: faker.number.int({ min: 300000, max: 500000 }),
    periodExpensesChange: faker.number.float({ min: -3, max: 10, fractionDigits: 1 }),
    
    nominalPL: faker.number.int({ min: 100000, max: 300000 }),
    nominalPLChange: faker.number.float({ min: -2, max: 12, fractionDigits: 1 }),
    
    overdue: faker.number.int({ min: 50000, max: 150000 }),
    overdueChange: faker.number.float({ min: -8, max: 5, fractionDigits: 1 }),
    
    badDebtProvisions: faker.number.int({ min: 20000, max: 60000 }),
    badDebtProvisionsChange: faker.number.float({ min: -10, max: 5, fractionDigits: 1 }),
    
    yearlyData,
    monthlyData,
    weeklyData,
    outstandingBreakdown
  };
};

export const generateBusinessKeyMetrics = (): BusinessKeyMetrics => {
  // Generate monthly historical data for the past 12 months
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const historicalData = months.map(month => ({
    month,
    arpu: faker.number.float({ min: 80, max: 140, fractionDigits: 2 }),
    cac: faker.number.float({ min: 60, max: 90, fractionDigits: 2 }),
    ltv: faker.number.float({ min: 250, max: 450, fractionDigits: 2 }),
    churn: faker.number.float({ min: 1, max: 6, fractionDigits: 1 }),
    margin: faker.number.float({ min: 50, max: 80, fractionDigits: 1 }),
  }));

  // Calculate current values (latest month)
  const currentMonth = historicalData[historicalData.length - 1];

  const totalCustomers = faker.number.int({ min: 5000, max: 10000 });
  const mrrPerCustomer = currentMonth.arpu;
  const monthlyRecurringRevenue = Math.round(totalCustomers * mrrPerCustomer);

  return {
    northStarMetric: {
      name: 'Policy Renewal Rate',
      value: faker.number.float({ min: 85, max: 95, fractionDigits: 1 }),
      change: faker.number.float({ min: -2, max: 5, fractionDigits: 1 }),
      goal: 95,
      unit: '%'
    },
    averageRevenuePerCustomer: currentMonth.arpu,
    customerAcquisitionCost: currentMonth.cac,
    lifetimeValue: currentMonth.ltv,
    grossMargin: currentMonth.margin,
    revenueGrowthRate: faker.number.float({ min: 3, max: 15, fractionDigits: 1 }),
    grossMerchandiseValue: faker.number.int({ min: 8000000, max: 15000000 }),
    churnRate: currentMonth.churn,
    totalRevenue: faker.number.int({ min: 8000000, max: 12000000 }),
    annualRecurringRevenue: monthlyRecurringRevenue * 12,
    monthlyRecurringRevenue,
    historicalData,
  };
};
